import { useEffect, useState, useRef } from 'react';
import socketio from 'socket.io-client';

const SOCKET_PATH = '/kf-mfg-socket/public/v1/socket';

/**
 * Custom hook for managing socket connections and handling socket events.
 * @param {object} options - The options for configuring the socket connection.
 * @param {string} [options.namespace] - The namespace for the socket connection.
 * @param {boolean} [options.enabled] - Whether the socket connection is enabled or not.
 * @param {string} [options.event] - The event to listen for on the socket connection. If omitted, no event listener is registered.
 */
export const useSocket = ({ namespace = 'production-request', event, enabled = true }) => {
  const socketRef = useRef(/** @type {import('socket.io-client').Socket | null}*/ (null));
  const [socketMessage, setSocketMessage] = useState(/** @type {Local.SocketMessage | null} */ (null));

  // Manage the socket connection lifecycle based on enabled/namespace only
  useEffect(() => {
    if (!enabled) {
      return () => null;
    }

    const server = `${window.REACT_APP_API_URL}/${namespace}`;
    const newSocket = socketio(server, {
      path: SOCKET_PATH,
      secure: true,
      rejectUnauthorized: false,
    });

    socketRef.current = newSocket;

    return () => {
      newSocket.close();
      socketRef.current = null;
    };
  }, [enabled, namespace]);

  // Manage event subscriptions separately to avoid unnecessary reconnects
  useEffect(() => {
    const socket = socketRef.current;
    if (!enabled || !socket || !event) {
      return () => null;
    }

    const handleEvent = (value) => {
      setSocketMessage(value);
    };

    socket.on(event, handleEvent);

    return () => {
      socket.off(event, handleEvent);
      setSocketMessage(null);
    };
  }, [enabled, event]);

  return { socketMessage, setSocketMessage };
};
